/**
 * React Bits — ShinyText, adapted from
 * https://reactbits.dev/text-animations/shiny-text
 *
 * A text span whose colour is a gradient with one bright band, slid across the
 * glyphs by animating `background-position`. `motion` drives the loop so no
 * stylesheet is needed. Renders as plain coloured text when `disabled` or when
 * the user prefers reduced motion.
 */

import { motion } from 'motion/react';
import type { CSSProperties, ReactNode } from 'react';

interface ShinyTextProps {
  children: ReactNode;
  /** Base text colour. */
  color?: string;
  /** Colour of the moving band. Defaults to a lighter wash of `color`. */
  highlight?: string;
  /** Seconds per sweep. */
  speed?: number;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function ShinyText({
  children,
  color = '#7C5BD9',
  highlight = 'rgba(255,255,255,0.85)',
  speed = 3,
  disabled = false,
  className,
  style,
}: ShinyTextProps) {
  const isStatic = disabled || prefersReducedMotion();

  if (isStatic) {
    return (
      <span className={className} style={{ ...style, color }}>
        {children}
      </span>
    );
  }

  return (
    <motion.span
      className={className}
      style={{
        ...style,
        display: 'inline-block',
        color: 'transparent',
        backgroundImage: `linear-gradient(120deg, ${color} 40%, ${highlight} 50%, ${color} 60%)`,
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
      }}
      initial={{ backgroundPosition: '150% 0' }}
      animate={{ backgroundPosition: '-50% 0' }}
      transition={{ duration: speed, ease: 'linear', repeat: Infinity, repeatDelay: 1.2 }}
    >
      {children}
    </motion.span>
  );
}

export default ShinyText;
